/**
 * Step containers styling utilities
 * Shared styling functions for step container components
 */

import { Theme } from '@mui/material';
import { STEP_CONTAINERS_CONSTANTS } from '../constants/step-containers.constants';
import { STEP_NODE_CONSTANTS } from '../constants/step-node.constants';

/**
 * Styles for the main steps container
 * Uses flexbox with spacing handled by margin-right on children
 */
export const getStepsContainerStyles = (
  stepCount: number,
  stepSpacing: number,
  theme: Theme,
) => {
  // Calculate node width in pixels
  const nodeWidth = theme.spacing(STEP_NODE_CONSTANTS.SIZE);

  const estimatedStepWidth = Math.max(Number(nodeWidth), 120);

  const calculatedWidth =
    stepCount * estimatedStepWidth + (stepCount - 1) * stepSpacing;

  const minWidth =
    stepCount >= 3 ? 3 * estimatedStepWidth + 2 * stepSpacing : calculatedWidth;

  return {
    display: 'flex',
    alignItems: 'center',
    position: 'relative' as const,
    width: `${calculatedWidth}px`, // Set explicit width to ensure all steps are included
    minWidth: `${minWidth}px`,
    marginLeft: 0,
    paddingLeft: 0,
  };
};

/**
 * Styles for individual step container
 */
export const getStepContainerStyles = (
  stepIndex: number,
  stepSpacing: number,
  theme: Theme,
  isLast: boolean,
  isFirst: boolean,
) => {
  const marginRight = isLast ? '0px' : `${stepSpacing}px`;
  const marginLeft = isFirst ? '0px' : undefined;

  return {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    position: 'relative' as const,
    width: 'fit-content',
    marginRight,
    marginLeft,
    flexShrink: 0,
    animation: `stepEntrance ${STEP_CONTAINERS_CONSTANTS.STEP_ENTRANCE.DURATION} ${STEP_CONTAINERS_CONSTANTS.STEP_ENTRANCE.EASING} ${stepIndex * STEP_CONTAINERS_CONSTANTS.STEP_ENTRANCE.STAGGER_DELAY}s forwards`,
    opacity: STEP_CONTAINERS_CONSTANTS.STEP_ENTRANCE.INITIAL.OPACITY,
    zIndex: STEP_CONTAINERS_CONSTANTS.Z_INDEX,
  };
};

/**
 * Styles for step label container
 */
export const getStepLabelContainerStyles = () => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center', // Always center labels under their nodes
  marginTop: STEP_CONTAINERS_CONSTANTS.LABEL_MARGIN_TOP,
});

/**
 * Styles for node wrapper
 */
export const getNodeWrapperStyles = () => ({
  position: 'relative' as const,
  zIndex: `${STEP_CONTAINERS_CONSTANTS.NODE_Z_INDEX} !important`,
});
